import { storeService } from "@/services/storeService";
import {
  ApiResponse,
  CommonParams,
  DEFAULT_SUCCESS_MESSAGE,
  OptionDto,
  StoreDto,
  StoreParams,
} from "@/types";
import { toastError, toastSuccess } from "@/utils/toastify";
import { create } from "zustand";

type ShopStore = {
  shops: StoreDto[];
  shop: StoreDto | null;
  shopsInCompany: { label: string; value: number }[];
  setShops: (shops: StoreDto[]) => void;
  setShopsInCompany: (shops: { label: string; value: number }[]) => void;
  createShop: (
    params: StoreParams
  ) => Promise<ApiResponse<{ store: StoreDto; user: any }>>;
  updateShop: (
    id: number,
    data: {
      name: string;
      address: string | "";
      group_id: number | null;
      phone?: string;
      report_history?: string;
      store_inspection_history?: string;
    }
  ) => Promise<ApiResponse<StoreDto>>;
  fetchStore: (id: number) => Promise<StoreDto | null>;
  fetchMyStore: () => Promise<StoreDto | null>;
  getStoreById: (id: number) => Promise<ApiResponse<StoreDto>>;
  fetchShopsInCompany: (
    params: CommonParams<{
      companyId: number;
      inIds?: number[];
      notInIds?: number[];
      groupId?: number;
    }>
  ) => Promise<ApiResponse<OptionDto[]>>;
  updateStoreStatus: (id: number) => Promise<ApiResponse<null>>;
  deleteStore: (id: number) => Promise<ApiResponse<null>>;
};

export const useShopStore = create<ShopStore>((set, get) => ({
  shops: [],
  shop: null,
  shopsInCompany: [],
  setShops: (shops: StoreDto[]) => set({ shops }),
  setShopsInCompany: (shops: { label: string; value: number }[]) =>
    set({ shopsInCompany: shops }),
  createShop: async (params: StoreParams) => {
    try {
      const response = await storeService.createStore(params);
      return response;
    } catch (error: any) {
      throw error;
    }
  },
  updateShop: async (
    id: number,
    data: {
      name: string;
      address: string | "";
      group_id: number | null;
      phone?: string;
      report_history?: string;
      store_inspection_history?: string;
    }
  ) => {
    const oldShop = get().shop;
    try {
      set(({ shop }) => ({
        shop: {
          ...(shop || {}),
          name: data.name,
          address: data.address || "",
          group: {
            ...(shop?.group || {}),
            id: data.group_id,
          },
          phone: data.phone,
          report_history: data.report_history,
          store_inspection_history: data.store_inspection_history,
        } as StoreDto,
      }));
      const response = await storeService.updateStore(id, data);
      return response;
    } catch (error: any) {
      set({ shop: oldShop });
      throw error;
    }
  },
  fetchStore: async (id: number) => {
    try {
      const resp = await storeService.getStoreById(id);
      set({ shop: resp.data });
      return resp.data;
    } catch (error: any) {
      return null;
    }
  },
  getStoreById: async (id: number) => {
    const response = await storeService.getStoreById(id);
    return response;
  },
  fetchMyStore: async () => {
    try {
      const resp = await storeService.getMyStore();
      set({ shop: resp.data });
      return resp.data;
    } catch (error: any) {
      return null;
    }
  },
  fetchShopsInCompany: async (
    params: CommonParams<{
      companyId: number;
      inIds?: number[];
      notInIds?: number[];
      groupId?: number;
    }>
  ) => {
    try {
      const { companyId, inIds, notInIds } = params;
      const response = await storeService.getMyStores({
        company_id: companyId,
        inIds: inIds || [],
        notInIds: notInIds || [],
        group_id: params?.groupId || undefined,
        ...params,
      });
      set({
        shopsInCompany: response.data.map((shop) => ({
          label: shop.label,
          value: shop.key,
        })),
      });
      return response;
    } catch (error) {
      return {
        data: [],
        pagination: {
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 0,
        },
      } as any;
    }
  },
  updateStoreStatus: async (id: number) => {
    try {
      set(({ shop }) => ({
        shop: {
          ...(shop || {}),
          status: shop?.status == "ENABLE" ? "DISABLE" : "ENABLE",
        } as StoreDto,
      }));
      const response = await storeService.updateStoreStatus(id);
      toastSuccess(response?.message || DEFAULT_SUCCESS_MESSAGE);
      return response;
    } catch (error: any) {
      toastError(error.message);
      return {
        data: null,
        error: error.message,
      } as any;
    }
  },
  deleteStore: async (id: number) => {
    const response = await storeService.deleteStore(id);
    return response;
  },
}));
